import React from 'react'
import { cn } from '@/lib/utils'

/**
 * Компактное операционное действие в строке таблицы/карточки.
 *
 * Обычный `Button size="sm"` — это `px-4 py-2 text-sm`: для строки списка он
 * слишком крупный, несколько таких действий переносятся на вторую строку и
 * простой операционный поток начинает выглядеть как машина состояний
 * (`docs/UI_ACTIONS_GUIDELINES.md`). Здесь фиксированная высота `h-8`,
 * `text-xs` и `whitespace-nowrap` — действия остаются в одну строку.
 *
 * `tone`:
 *  - `primary` — следующий нормальный шаг, один на строку;
 *  - `secondary` — обычное действие;
 *  - `menu` — пункт выпадающего «Ещё» (растягивается на ширину меню).
 *
 * Стиль повторяет `CaseActionButton` из `CrmAccountTransfersPage` — эталон
 * гайдлайна; ту страницу стоит перевести на этот компонент отдельным заходом.
 */
export function ActionButton({
  children,
  icon,
  tone = 'secondary',
  className,
  ...props
}: {
  children: React.ReactNode
  icon?: React.ReactNode
  tone?: 'primary' | 'secondary' | 'menu'
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:pointer-events-none disabled:opacity-50',
        tone === 'primary' && 'bg-primary text-primary-foreground hover:bg-primary/90',
        tone === 'secondary' && 'border border-border bg-background text-foreground hover:bg-muted/60',
        tone === 'menu' && 'w-full justify-start rounded px-2 py-1.5 text-left text-xs hover:bg-muted/70',
        className
      )}
      {...props}
    >
      {icon}
      <span>{children}</span>
    </button>
  )
}
